import { IResPag } from "utils/types"
import { IPaginationReq } from "utils/interfaces"
import { TAGS, api } from "../../api"
import {
  IMission,
  IMissionChest,
  IMissionChestInstance,
  IMissionPoints,
  IMissionProgress,
  MissionType
} from "./types"

const basePath = "/missions"

export const missionsApi = api.enhanceEndpoints({
  addTagTypes: [TAGS.MISSIONS, TAGS.POINTS_LEADERBOARD]
})

export const missionsEndpoints = missionsApi.injectEndpoints({
  endpoints: (builder) => ({
    getMissions: builder.query<IResPag<IMission>, IPaginationReq>({
      query: (params) => {
        return {
          url: `${basePath}`,
          method: "GET",
          params
        }
      },
      providesTags: (_, __, { page, limit }) => [
        { type: TAGS.MISSIONS, id: `LIST` },
        { type: TAGS.MISSIONS, id: `LIST:page:${page}:limit:${limit}` }
      ]
    }),
    getMissionsByType: builder.query<IMission[], MissionType>({
      query: (missionType) => ({
        url: `${basePath}/type/${missionType}`,
        method: "GET"
      }),
      providesTags: (_, __, missionType) => [
        { type: TAGS.MISSIONS, id: `MISSION:${missionType}` }
      ]
    }),
    getMyMissionProgress: builder.query<IMissionProgress[], void>({
      query: () => ({
        url: `${basePath}/me`,
        method: "GET"
      }),
      providesTags: () => [{ type: TAGS.MISSIONS, id: "MY_PROGRESS" }]
    }),
    getMyMissionProgressByType: builder.query<IMissionProgress[], MissionType>({
      query: (missionType) => ({
        url: `${basePath}/type/${missionType}/me`,
        method: "GET"
      }),
      providesTags: (_, __, missionType) => [
        { type: TAGS.MISSIONS, id: `MY_PROGRESS:${missionType}` }
      ]
    }),
    getMyPointsBalance: builder.query<IMissionPoints | null, void>({
      query: () => ({
        url: `${basePath}/me/balance`,
        method: "GET"
      }),
      providesTags: () => [{ type: TAGS.MISSIONS, id: "MY_POINTS_BALANCE" }]
    }),
    claimMission: builder.mutation<void, IMission>({
      query: (mission) => ({
        url: `${basePath}/${mission._id}/claim`,
        method: "PATCH"
      }),
      invalidatesTags: () => [
        { type: TAGS.MISSIONS, id: "HAS_UNCLAIMED" },
        { type: TAGS.POINTS_LEADERBOARD, id: "LIST" }
      ],
      onQueryStarted: async (mission, { queryFulfilled, dispatch }) => {
        await queryFulfilled

        dispatch(
          missionsEndpoints.util.updateQueryData(
            "getMyPointsBalance",
            undefined,
            (draft) => {
              if (!draft) {
                return {
                  _id: "",
                  createdAt: "",
                  updatedAt: "",

                  userId: "",
                  value: mission.points,
                  total: mission.points
                }
              }

              draft.value += mission.points
              draft.total += mission.points
            }
          )
        )

        dispatch(
          missionsEndpoints.util.updateQueryData(
            "getMyMissionProgress",
            undefined,
            (draft) => {
              if (!draft) return

              const missionItem = draft.find(
                (item) => item.missionId === mission._id
              )

              if (!missionItem) return

              missionItem.isCompleted = true
              missionItem.isClaimed = true
              missionItem.completedAt = new Date().toISOString()
              missionItem.claimedAt = new Date().toISOString()
            }
          )
        )
      }
    }),
    getMissionChests: builder.query<IMissionChest[], void>({
      query: () => ({
        url: `${basePath}/chest`,
        method: "GET"
      }),
      providesTags: () => [{ type: TAGS.MISSIONS, id: "CHESTS" }]
    }),
    getMissionMyChests: builder.query<IMissionChestInstance[], void>({
      query: () => ({
        url: `${basePath}/chest-instances`,
        method: "GET"
      }),
      providesTags: () => [{ type: TAGS.MISSIONS, id: "MY_CHESTS" }]
    }),
    hasUnclaimedMissions: builder.query<{ hasUnclaimed: boolean }, void>({
      query: () => ({
        url: `${basePath}/me/has-unclaimed`,
        method: "GET"
      }),
      providesTags: () => [{ type: TAGS.MISSIONS, id: "HAS_UNCLAIMED" }]
    }),
    claimMissionChest: builder.mutation<void, IMissionChest>({
      query: (chest) => ({
        url: `${basePath}/chest/claim/${chest._id}`,
        method: "PATCH"
      }),
      onQueryStarted: async (chest, { queryFulfilled, dispatch }) => {
        try {
          await queryFulfilled

          dispatch(
            missionsEndpoints.util.updateQueryData(
              "getMyPointsBalance",
              undefined,
              (draft) => {
                if (!draft) {
                  return {
                    _id: "",
                    createdAt: "",
                    updatedAt: "",

                    userId: "",
                    value: 0,
                    total: 0
                  }
                }

                draft.value -= chest.pointsPrice
              }
            )
          )

          dispatch(
            missionsEndpoints.util.updateQueryData(
              "getMissionMyChests",
              undefined,
              (draft) => {
                if (!draft) return

                const chestItem = draft.find((item) => item.type === chest.type)

                if (!chestItem) {
                  draft.push({
                    _id: "",
                    createdAt: "",
                    updatedAt: "",
                    ownerId: "",
                    type: chest.type,
                    count: 1
                  })
                  return
                }

                chestItem.count += 1
              }
            )
          )

          // dispatch(
          //   missionsEndpoints.util.updateQueryData(
          //     "getMissionChests",
          //     undefined,
          //     (draft) => {
          //       if (!draft) return

          //       const chestItem = draft.find((item) => item._id === chest._id)

          //       if (!chestItem) return

          //       chestItem.remainingCount -= 1
          //     }
          //   )
          // )
        } catch {
          // Error is handled in the component
        }
      }
    })
  })
})

export const {
  useGetMissionsQuery,
  useClaimMissionMutation,
  useGetMissionsByTypeQuery,
  useGetMyMissionProgressByTypeQuery,
  useGetMyMissionProgressQuery,
  useGetMyPointsBalanceQuery,
  useGetMissionChestsQuery,
  useGetMissionMyChestsQuery,
  useClaimMissionChestMutation,
  useHasUnclaimedMissionsQuery
} = missionsEndpoints
